import { Color, Link, Text } from '@billy/design-system'
import { useCallback, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { BalanceType } from '../../types/balanceType'
import { ReportRowType } from '../../types/reportRowType'
import * as Styled from './styles'
import { getFormattedBalance } from './utils/getFormattedBalance'

interface BalanceCellProps {
  balance: number | undefined
  balanceType?: BalanceType
  id: string
  /* only applicable to P&L for now */
  isFirstCell?: boolean
  periodQuery?: string
  type: ReportRowType
}

export const BalanceCell = ({ balance, balanceType, id, isFirstCell, periodQuery, type }: BalanceCellProps) => {
  const { organization } = useUserOrganization()
  const isBalanceTypeRelative = balanceType === 'percentageChange' || balanceType === 'percentageOfType'
  const isHeaderOrSum = type === ReportRowType.Header || type === ReportRowType.Footer
  const isConsolidatedType = balanceType === 'debit' || balanceType === 'credit' || isBalanceTypeRelative

  const getAccountUrl = useCallback(
    (accountId: string) =>
      getOrganizationViewUrl(
        organization?.url,
        reactRoute.organization.accounts.getAccountRoute(accountId),
        periodQuery ? { period: periodQuery } : undefined,
      ),
    [organization?.url, periodQuery],
  )

  const textColor = useMemo(() => {
    if (balanceType === 'percentageChange' && !!balance) {
      return balance > 0 ? Color.Green700 : Color.Red700
    }
  }, [balance, balanceType])

  const balanceFormatted = useMemo(
    () => getFormattedBalance(balance || 0, isBalanceTypeRelative),
    [balance, isBalanceTypeRelative],
  )

  return (
    <Styled.TableCell nested={isBalanceTypeRelative} shouldHaveBorder={isFirstCell && isConsolidatedType}>
      {type === ReportRowType.Account && !isConsolidatedType ? (
        <Link href={getAccountUrl(id)} withAnimation target="_blank">
          {balanceFormatted}
        </Link>
      ) : (
        <Text variant={isHeaderOrSum ? 'h3' : 'bodyMD'} color={textColor}>
          {balanceFormatted}
        </Text>
      )}
    </Styled.TableCell>
  )
}
